import { Broker, CodeDeployment, BrokerAutoScalingGroupInstance } from '@amzn/amazonmq-opsconsole-client';
import {
    Box,
    Flashbar,
    SpaceBetween,
} from '@amzn/awsui-components-react';
import React, { useCallback, useMemo } from 'react';
import { InstanceVisualizationNode, StatusIndicatorColor, StatusIndicatorState } from '../../../../types';
import NodeRenderer from './NodeRenderer';

interface Props {
    broker: Broker;
    instances: BrokerAutoScalingGroupInstance[];
    asgLoading: boolean;
    asgError: Error | null;
    deployments: CodeDeployment[];
    codeDeployLoading: boolean;
    codeDeployError: Error | null;
}

const AsgComponent: React.FC<Props> = React.memo(({
    broker,
    instances,
    asgLoading,
    asgError,
    deployments,
    codeDeployLoading,
    codeDeployError
}) => {

    const getStatusColor = useCallback((state: string): StatusIndicatorColor => {
        const isValidStatusIndicatorState = (s: string): s is StatusIndicatorState => {
            return s in StatusIndicatorColor;
        };
        
        return isValidStatusIndicatorState(state) 
            ? StatusIndicatorColor[state] 
            : StatusIndicatorColor.Detached;
    }, []);

    const processInstances = useCallback((): InstanceVisualizationNode[] => {
        if (!Array.isArray(instances) || instances.length === 0) {
            return [];
        }

        const pendingNodes = instances.filter(i => i?.lifecycleState?.includes('Pending'));
        const terminatingNodes = instances.filter(i => i?.lifecycleState?.includes('Terminating'));
        const regularNodes = instances.filter(i =>
            i?.lifecycleState &&
            !i.lifecycleState.includes('Terminating') &&
            !i.lifecycleState.includes('Pending')
        );

        const result: InstanceVisualizationNode[] = [];

        pendingNodes.forEach(pendingInstance => {
            result.push({
                type: 'replacement',
                pendingInstance,
                terminatingInstance: terminatingNodes.length > 0 ? terminatingNodes.shift() : undefined
            });
        });

        terminatingNodes.forEach(instance => {
            result.push({ type: 'regular', instance });
        });

        regularNodes.forEach(instance => {
            result.push({ type: 'regular', instance });
        });

        return result;
    }, [instances]);

    const processedNodes = useMemo(() => processInstances(), [processInstances]);

    const nodeProps = useMemo(() => ({
        deployments,
        asgLoading,
        broker,
        getStatusColor
    }), [deployments, asgLoading, broker, getStatusColor]);

    const renderNodes = useCallback(() => {
        const nodes = processedNodes;

        if (nodes.length === 0) {
            return undefined;
        }

        if (nodes.length === 1) {
            return <NodeRenderer node={nodes[0]} {...nodeProps} />;
        }

        return (
            <>
                <NodeRenderer node={nodes[0]} {...nodeProps} />
                <SpaceBetween direction="horizontal" size='xl'>
                    {nodes.slice(1, 3).map((node, index) => (
                        <NodeRenderer
                            key={`node-${index + 1}`}
                            node={node}
                            {...nodeProps}
                        />
                    ))}
                </SpaceBetween>
            </>
        );
    }, [processedNodes, nodeProps]);

    if (asgError) {
        return (
            <Flashbar
                items={
                    [{
                        "type": "error",
                        "dismissible": false,
                        "content": "Error loading instances: " + asgError.message || 'Unknown error' + " " + asgError.stack
                    }]
                }
            />
        );
    }

    if (!instances || instances.length === 0) {
        return (
            <Box textAlign="center" padding="l">
                No instances available
            </Box>
        );
    }

    return (
        <SpaceBetween direction='vertical' size='l' alignItems="center">
            {codeDeployError && (
                <Flashbar
                    items={
                        [{
                            "type": "error",
                            "dismissible": false,
                            "content": "Error loading deployments: " + codeDeployError.message || 'Unknown error' + " " + codeDeployError.stack || ''
                        }]
                    }
                />
            )}
            {renderNodes()}
        </SpaceBetween>
    );
});

export default AsgComponent;
